import { createSlice } from '@reduxjs/toolkit';

import {
  createDraftApplicationThunk,
  uploadResumeThunk,
  getResumePrefillStatusThunk,
  uploadVideoThunk,
  updateCandidateProfileThunk,
  submitApplicationThunk,
} from '@/modules/candidate/store/applicationThunk';

import type {
  CandidateApplicationState,
} from '@/modules/candidate/types/candidateApplication.types';


const initialState: CandidateApplicationState = {
  application: null,

  job: null,

  applicationId: null,

  resume: null,

  video: null,

  draftId: null,

  isNewApplication: false,

  isResumedDraft: false,

  loading: false,

  uploading: false,

  error: null,

  resumePrefillStatus: 'IDLE',

  resumePrefillProgress: 0,

  resumePrefillError: null,

  resumeParsedData: null,

  resumeParsedText: null,

  resumeParserVersion: null,

  resumeExtractionVersion: 0,

  resumePrefillStartedAt: null,

  resumePrefillCompletedAt: null,

  status: 'idle',
};


const applicationSlice = createSlice({
  name: 'application',

  initialState,

  reducers: {
    clearApplication(state) {
      state.application = null;

      state.job = null;

      state.applicationId = null;

      state.draftId = null;

      state.resume = null;

      state.video = null;

      state.isNewApplication = false;

      state.isResumedDraft = false;

      state.loading = false;

      state.uploading = false;

      state.error = null;

      state.resumePrefillStatus = 'IDLE';

      state.resumePrefillProgress = 0;

      state.resumePrefillError = null;

      state.resumeParsedData = null;

      state.resumeParsedText = null;

      state.resumeParserVersion = null;

      state.resumeExtractionVersion = 0;

      state.resumePrefillStartedAt = null;

      state.resumePrefillCompletedAt = null;

      state.status = 'idle';
    },


   setDraftData(state, action) {
  const draft = action.payload;

  state.application = {
    ...draft,

    resumeParsedData:
      draft.resumeParsedData || null,

    resumeParsedText:
      draft.resumeParsedText || null,
  };

  state.draftId = draft.id;

  state.resume =
    draft.resumeData || null;

  state.video =
    draft.videoData || null;

  state.resumePrefillStatus =
    draft.resumePrefillStatus || 'IDLE';

  state.resumePrefillProgress =
    draft.resumePrefillProgress ?? 0;

  state.resumePrefillError =
    draft.resumePrefillError || null;

  state.resumeParsedData =
    draft.resumeParsedData || null;

  state.resumeParsedText =
    draft.resumeParsedText || null;

  state.resumeParserVersion =
    draft.resumeParserVersion || null;

  state.resumeExtractionVersion =
    draft.resumeExtractionVersion ?? 0;

  state.resumePrefillStartedAt =
    draft.resumePrefillStartedAt || null;

  state.resumePrefillCompletedAt =
    draft.resumePrefillCompletedAt || null;
  },
},


  extraReducers: (builder) => {
    builder

      // CREATE DRAFT
      .addCase(
        createDraftApplicationThunk.fulfilled,
        (state, action) => {
          state.loading = false;

          const data = action.payload.data;

          state.application =
            data.draft;

          state.draftId =
            data.draft.id;

          state.job =
            data.job;

          state.resume =
            data.draft.resumeData || null;

          state.video =
            data.draft.videoData || null;

          state.isNewApplication =
            data.isNewDraft;

          state.isResumedDraft =
            data.isResumedDraft;

          state.resumePrefillStatus =
            data.draft.resumePrefillStatus || 'IDLE';

          state.resumePrefillProgress =
            data.draft.resumePrefillProgress ?? 0;

          state.resumePrefillError =
            data.draft.resumePrefillError || null;

          state.resumeParsedData =
            data.draft.resumeParsedData || null;

          state.resumeParsedText =
            data.draft.resumeParsedText || null;

          state.resumeParserVersion =
            data.draft.resumeParserVersion || null;

          state.resumeExtractionVersion =
            data.draft.resumeExtractionVersion ?? 0;

          state.resumePrefillStartedAt =
            data.draft.resumePrefillStartedAt || null;

          state.resumePrefillCompletedAt =
            data.draft.resumePrefillCompletedAt || null;

          state.status =
            'draft-created';
        }
      )


      .addCase(
        createDraftApplicationThunk.rejected,
        (state, action: any) => {
          state.loading = false;

          state.error =
            action.payload ||
            'Failed to create draft application.';
        }
      )


      // UPDATE PROFILE
      .addCase(
        uploadResumeThunk.pending,
        (state) => {
          state.uploading = true;
          state.error = null;

          // IMPORTANT:
          // Previous resume ka parsed data clear karo
          state.resumePrefillStatus = 'QUEUED';
          state.resumePrefillProgress = 0;
          state.resumePrefillError = null;

          state.resumeParsedData = null;
          state.resumeParsedText = null;
          state.resumeParserVersion = null;

          state.resumePrefillStartedAt = null;
          state.resumePrefillCompletedAt = null;

          // New resume ke liye new extraction cycle
          state.resumeExtractionVersion =
            state.resumeExtractionVersion + 1;
        }
      )


      .addCase(
        updateCandidateProfileThunk.fulfilled,
        (state, action) => {
          state.loading = false;

          /*
           * Keep the complete draft in state.
           * The API response is the ApiResponse object,
           * therefore use .data.draft when available.
           */
          const draft =
            action.payload?.data?.draft ||
            action.payload?.draft ||
            action.payload?.data ||
            action.payload;

          state.application =
            draft;

          state.draftId =
            draft?.id || state.draftId;

          state.status =
            'candidate-profile-updated';
        }
      )


      .addCase(
        updateCandidateProfileThunk.rejected,
        (state, action: any) => {
          state.loading = false;

          state.error =
            action.payload ||
            'Failed to update candidate profile.';
        }
      )



      .addCase(
        uploadResumeThunk.fulfilled,
        (state, action) => {
          state.uploading = false;

          const data =
            action.payload?.data;

          const draft =
            data?.draft;

          /*
           * uploadResume API returns:
           *
           * {
           *   data: {
           *     draft,
           *     isNewResume,
           *     isReplaced
           *   }
           * }
           */
          if (draft) {
            state.application = {
              ...draft,

              // IMPORTANT:
              // Newly uploaded resume ka parsing abhi complete nahi hua.
              // Backend agar old parsed data return kare bhi,
              // application state me usko retain mat karo.
              resumeParsedData: null,
              resumeParsedText: null,
              resumeParserVersion: null,

              resumePrefillStatus:
                draft.resumePrefillStatus === 'COMPLETED'
                  ? 'QUEUED'
                  : draft.resumePrefillStatus || 'QUEUED',

              resumePrefillProgress: 0,

              resumePrefillError: null,

              resumePrefillStartedAt: null,

              resumePrefillCompletedAt: null,
            };

            state.draftId =
              draft.id ||
              state.draftId;

            state.resume =
              draft.resumeData || null;

            state.resumePrefillStatus = 'QUEUED';

            state.resumePrefillProgress = 0;

            state.resumePrefillError = null;

            state.resumeParsedData = null;

            state.resumeParsedText = null;

            state.resumeParserVersion = null;

            /*
             * Keep backend extraction version if available.
             * Do not manufacture a version here.
             */
            state.resumeExtractionVersion =
              draft.resumeExtractionVersion ??
              state.resumeExtractionVersion;

            state.resumePrefillStartedAt = null;

            state.resumePrefillCompletedAt = null;
          }

          state.status =
            'resume-uploaded';
        }
      )


      .addCase(
        uploadResumeThunk.rejected,
        (state, action: any) => {
          state.uploading = false;

          state.error =
            action.payload ||
            'Resume upload failed.';
        }
      )


      // RESUME PREFILL STATUS
      .addCase(
        getResumePrefillStatusThunk.pending,
        (state) => {
          state.error = null;
        }
      )


      .addCase(
        getResumePrefillStatusThunk.fulfilled,
        (state, action) => {
          const data =
            action.payload?.data;

          if (!data) {
            return;
          }

          /*
           * IMPORTANT:
           * Backend har resume extraction ke liye
           * resumeExtractionVersion deta hai.
           *
           * Agar polling response purane resume ka hai,
           * us response ko ignore karna hai.
           */
          const incomingVersion =
            data.resumeExtractionVersion;

          const currentVersion =
            state.resumeExtractionVersion;

          /*
           * Old polling response protection.
           *
           * Example:
           * Current resume = version 2
           * Late response  = version 1
           *
           * Version 1 ko Redux mein apply nahi karna.
           */
          if (
            incomingVersion !== undefined &&
            incomingVersion !== null &&
            currentVersion !== undefined &&
            currentVersion !== null &&
            incomingVersion < currentVersion
          ) {
            return;
          }

          /*
           * Latest/current resume ka polling result
           * ab Redux mein safely apply kar sakte hain.
           */
          state.resumePrefillStatus =
            data.resumePrefillStatus || 'IDLE';

          state.resumePrefillProgress =
            data.resumePrefillProgress ?? 0;

          state.resumePrefillError =
            data.resumePrefillError || null;

          state.resumeParsedData =
            data.resumeParsedData || null;

          state.resumeParsedText =
            data.resumeParsedText || null;

          state.resumeParserVersion =
            data.resumeParserVersion || null;

          state.resumeExtractionVersion =
            incomingVersion ?? currentVersion ?? 0;

          state.resumePrefillStartedAt =
            data.resumePrefillStartedAt || null;

          state.resumePrefillCompletedAt =
            data.resumePrefillCompletedAt || null;

          /*
           * Keep application draft metadata
           * synchronized with the latest valid
           * polling response.
           */
          if (state.application) {
            state.application = {
              ...state.application,

              resumePrefillStatus:
                data.resumePrefillStatus,

              resumePrefillProgress:
                data.resumePrefillProgress,

              resumePrefillError:
                data.resumePrefillError,

              resumeParsedData:
                data.resumeParsedData,

              resumeParsedText:
                data.resumeParsedText,

              resumeParserVersion:
                data.resumeParserVersion,

              resumeExtractionVersion:
                incomingVersion ??
                currentVersion ??
                0,

              resumePrefillStartedAt:
                data.resumePrefillStartedAt,

              resumePrefillCompletedAt:
                data.resumePrefillCompletedAt,
            };
          }

          if (
            data.resumePrefillStatus ===
            'COMPLETED'
          ) {
            state.status =
              'resume-prefill-completed';
          }
        }
      )

      .addCase(
        getResumePrefillStatusThunk.rejected,
        (state, action: any) => {
          state.error =
            action.payload ||
            'Failed to fetch resume prefill status.';
        }
      )


      // VIDEO UPLOAD
      .addCase(
        uploadVideoThunk.pending,
        (state) => {
          state.uploading = true;

          state.error = null;
        }
      )


      .addCase(
        uploadVideoThunk.fulfilled,
        (state, action) => {
          state.uploading = false;

          const data =
            action.payload?.data;

          const draft =
            data?.draft;

          if (draft) {
            state.application =
              draft;

            state.draftId =
              draft.id ||
              state.draftId;

            state.video =
              draft.videoData || null;
          } else {
            state.video =
              action.payload;
          }

          state.status =
            'video-uploaded';
        }
      )


      .addCase(
        uploadVideoThunk.rejected,
        (state, action: any) => {
          state.uploading = false;

          state.error =
            action.payload ||
            'Video upload failed.';
        }
      )


      // SUBMIT APPLICATION
      .addCase(
        submitApplicationThunk.pending,
        (state) => {
          state.loading = true;

          state.error = null;
        }
      )


      .addCase(
        submitApplicationThunk.fulfilled,
        (state, action) => {
          state.loading = false;

          state.application =
            action.payload.application;

          state.status =
            'success';
        }
      )


      .addCase(
        submitApplicationThunk.rejected,
        (state, action: any) => {
          state.loading = false;

          state.error =
            action.payload ||
            'Failed to submit application.';
        }
      );
  },
});


export const {
  clearApplication,
  setDraftData,
} = applicationSlice.actions;


export default applicationSlice.reducer;